import { useMemo, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import type { ChartOptions, Plugin, TooltipItem } from "chart.js";
import NoDataFound from "@/components/NoDataFound/NoDataFound";
import styles from "./ApplicationFunnel.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const STAGE_COLOR = "#1f2937";
const SUCCESS_COLOR = "#2e7d32";
const DANGER_COLOR = "#c62828";
const LABEL_COLOR = "#374151";
const VALUE_COLOR = "#111827";
const PERCENT_COLOR = "#9ca3af";
const BAR_THICKNESS = 6;
const LABEL_GAP = 10;

const getTrackColor = () =>
  `rgba(${getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim()}, 0.14)`;

interface FunnelStage {
  label: string;
  count: number;
  percent: number;
  tone?: "success" | "danger";
}

const getStageColor = (tone: FunnelStage["tone"]) =>
  tone === "success" ? SUCCESS_COLOR : tone === "danger" ? DANGER_COLOR : STAGE_COLOR;

// Chart.js's bar element has no built-in unfilled "track" (the remainder of
// the row behind the value) or a text line above each bar — these two small
// plugins draw them directly on the canvas, so the funnel is still a real
// Chart.js render rather than a hand-rolled CSS progress bar. Both take
// their data as arguments (rather than reading a module-level constant, the
// way this started) since `stages` is now real data passed in as a prop.
const trackPlugin = (trackColor: string): Plugin<"bar"> => ({
  id: "funnelTrack",
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);
    ctx.save();
    ctx.fillStyle = trackColor;
    meta.data.forEach((bar) => {
      const { height } = bar.getProps(["height"], true);
      const radius = height / 2;
      ctx.beginPath();
      ctx.roundRect(chartArea.left, bar.y - radius, chartArea.right - chartArea.left, height, radius);
      ctx.fill();
    });
    ctx.restore();
  },
});

const labelPlugin = (stagesRef: { current: FunnelStage[] }): Plugin<"bar"> => ({
  id: "funnelLabels",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);
    ctx.save();
    ctx.textBaseline = "alphabetic";
    meta.data.forEach((bar, index) => {
      const stage = stagesRef.current[index];
      if (!stage) return;
      const { height } = bar.getProps(["height"], true);
      const textY = bar.y - height / 2 - LABEL_GAP;

      ctx.font = "500 12px 'DM Sans', sans-serif";
      ctx.fillStyle = LABEL_COLOR;
      ctx.textAlign = "left";
      ctx.fillText(stage.label, chartArea.left, textY);

      const percentText = `${stage.percent}%`;
      ctx.font = "400 12px 'DM Sans', sans-serif";
      ctx.fillStyle = PERCENT_COLOR;
      ctx.textAlign = "right";
      ctx.fillText(percentText, chartArea.right, textY);
      const percentWidth = ctx.measureText(percentText).width;

      ctx.font = "700 12px 'DM Sans', sans-serif";
      ctx.fillStyle = VALUE_COLOR;
      ctx.textAlign = "right";
      ctx.fillText(String(stage.count), chartArea.right - percentWidth - 8, textY);
    });
    ctx.restore();
  },
});

const toPercent = (count: number, total: number) => (total > 0 ? Math.round((count / total) * 100) : 0);

export interface ApplicationFunnelData {
  activeUser: number;
  blockedUser: number;
  underReviewUser: number;
  totalUser: number;
  rejectedUser?: number;
}

export interface ApplicationFunnelProps {
  data: ApplicationFunnelData | null;
  isLoading?: boolean;
}

const ApplicationFunnel = ({ data, isLoading = false }: ApplicationFunnelProps) => {
  const stages: FunnelStage[] = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Total applicants", count: data.totalUser, percent: 100 },
      {
        label: "Active members",
        count: data.activeUser,
        percent: toPercent(data.activeUser, data.totalUser),
        tone: "success",
      },
      {
        label: "Under review",
        count: data.underReviewUser,
        percent: toPercent(data.underReviewUser, data.totalUser),
      },
      {
        label: "Blocked",
        count: data.blockedUser,
        percent: toPercent(data.blockedUser, data.totalUser),
        tone: "danger",
      },
      {
        label: "Rejected",
        count: data.rejectedUser ?? 0,
        percent: toPercent(data.rejectedUser ?? 0, data.totalUser),
        tone: "danger",
      },
    ];
  }, [data]);

  const stagesRef = useRef(stages);
  stagesRef.current = stages;

  const chartData = useMemo(
    () => ({
      labels: stages.map((stage) => stage.label),
      datasets: [
        {
          label: "Applicants",
          data: stages.map((stage) => stage.percent),
          backgroundColor: stages.map((stage) => getStageColor(stage.tone)),
          borderRadius: 999,
          borderSkipped: false,
          maxBarThickness: BAR_THICKNESS,
        },
      ],
    }),
    [stages],
  );


  const plugins = useMemo(() => [trackPlugin(getTrackColor()), labelPlugin(stagesRef)], []);

  const options: ChartOptions<"bar"> = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 5, bottom: 5 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#ffffff",
        titleColor: VALUE_COLOR,
        bodyColor: VALUE_COLOR,
        borderColor: "#e5e7eb",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        displayColors: false,
        titleFont: { family: "DM Sans", weight: "bold" },
        bodyFont: { family: "DM Sans" },
        callbacks: {
          label: (ctx: TooltipItem<"bar">) => {
            const stage = stages[ctx.dataIndex];
            return stage ? `${stage.count} applicants (${stage.percent}%)` : "";
          },
        },
      },
    },
    scales: {
      x: { display: false, min: 0, max: 100 },
      y: { display: false },
    },
  };

  const isEmpty = !data || data.totalUser === 0;

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>Application Funnel</h2>
      <div className={styles.chartWrapper}>
        {!isLoading && isEmpty ? <NoDataFound /> : <Bar data={chartData} options={options} plugins={plugins} />}
      </div>
    </div>
  );
};

export default ApplicationFunnel;
